import React, { useState, useRef, useEffect, useCallback } from 'react';
import './ImageCropperModal.css';

const ImageCropperModal = ({ imageFile, onCropComplete, onCancel }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  const CIRCLE_SIZE = 300; // Size of the circular preview
  const MIN_SCALE = 0.1;
  const MAX_SCALE = 3;

  useEffect(() => {
    if (imageFile && imageRef.current) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          imageRef.current.src = img.src;
          
          // Calculate scale to fit the image in the circle
          const scaleX = CIRCLE_SIZE / img.width;
          const scaleY = CIRCLE_SIZE / img.height;
          const fitScale = Math.max(scaleX, scaleY);
          
          // Start at the fit scale (user can zoom in or out from here)
          setScale(fitScale);
          setOffset({ x: 0, y: 0 });
          setImageLoaded(true);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - offset.x,
      y: e.clientY - offset.y
    });
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - offset.x,
      y: touch.clientY - offset.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    setOffset({ x: newX, y: newY });
  }, [isDragging, dragStart]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    
    setOffset({ x: newX, y: newY });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  const handleWheel = (e) => {
    e.preventDefault();
    const scaleChange = e.deltaY > 0 ? -0.05 : 0.05;
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale + scaleChange));
    setScale(newScale);
  };

  const handleZoomChange = (e) => {
    setScale(parseFloat(e.target.value));
  };

  const cropImage = () => {
    if (!imageRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    // Set canvas to high resolution for better quality
    const outputSize = 400; // Higher resolution output
    canvas.width = outputSize;
    canvas.height = outputSize;

    // Fill with white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate scaling for output
    const outputScale = outputSize / CIRCLE_SIZE;

    // Draw image with offset and scale
    const scaledWidth = img.width * scale * outputScale;
    const scaledHeight = img.height * scale * outputScale;
    const x = (offset.x * outputScale) + (outputSize / 2) - (scaledWidth / 2);
    const y = (offset.y * outputScale) + (outputSize / 2) - (scaledHeight / 2);

    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

    // Convert to blob
    canvas.toBlob((blob) => {
      onCropComplete(blob);
    }, 'image/jpeg', 0.95);
  };

  return (
    <div className="cropper-modal-overlay">
      <div className="cropper-modal-content">
        <div className="cropper-header">
          <h3>Crop Your Image</h3>
          <button className="cropper-close" onClick={onCancel}>×</button>
        </div>

        <div className="cropper-body">
          <div className="cropper-instructions">
            <p>📌 Drag to reposition • 🔄 Scroll to zoom • Circle shows the final result</p>
          </div>

          <div
            className="cropper-container"
            onWheel={handleWheel}
            style={{ touchAction: 'none' }}
          >
            <div className="cropper-preview-area">
              <img
                ref={imageRef}
                className="cropper-image"
                alt="Crop preview"
                draggable={false}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                style={{
                  transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  opacity: imageLoaded ? 1 : 0,
                  transition: imageLoaded ? 'none' : 'opacity 0.3s ease',
                }}
              />
              <div className="cropper-circle-overlay" />
            </div>
          </div>

          <div className="cropper-controls">
            <div className="zoom-control">
              <label>Zoom:</label>
              <input
                type="range"
                min={MIN_SCALE}
                max={MAX_SCALE}
                step="0.01"
                value={scale}
                onChange={handleZoomChange}
                className="zoom-slider"
              />
              <span className="zoom-value">{(scale * 100).toFixed(0)}%</span>
            </div>

            <div className="crop-actions">
              <button onClick={onCancel} className="crop-cancel-btn">
                Cancel
              </button>
              <button onClick={cropImage} className="crop-save-btn">
                Crop & Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden canvas for cropping */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default ImageCropperModal;
